
/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useState, useMemo, useRef, useContext } from "react";
import { v4 as uuid } from "uuid";

import { LoginContext } from '../../context/login-context';
// const { DragDropContext, Draggable, Droppable } = window.ReactBeautifulDnd;
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import RebatesClient from "../../RebatesClient";

import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import moment from "moment";

// import { css } from "./css";
import { status } from "./testdata";
import Column from "../../components/drag-n-drop/Column";
import InvoiceModal from "./InvoiceModal.js";
import UploadModal from "./UploadModal.js";
import useInvoices from "../../hooks/use-invoices.js";
import { Box, Button, Checkbox, Chip, createTheme, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Snackbar, Alert, ThemeProvider } from "@mui/material";

import Grid from '@mui/material/Grid2'
import { GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import useReports from "../../hooks/use-reports.js";
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import useFarms from "../../hooks/use-farms.js";
import useStrategicAccounts from "../../hooks/use-strategic-accounts.js";
import Filters from "../../components/filters/filters.js";
import { DataGrid } from "../../components/index.js";
import usePersistedState from "../../hooks/use-persisted-state.js";
import ColumnsPanel from "../../components/datagrid/columns-panel.js";

import css from "./main.module.css";
import { currencyFormatted } from "../../util.js";
import {
  CUSTOMER_TYPE_CHIP_COLORS,
  CUSTOMER_TYPE_FILTER_OPTIONS,
  CUSTOMER_TYPE_LABELS,
  CUSTOMER_TYPE_NONE_FILTER_VALUE,
} from "../../components/constants.js";

dayjs.extend(utc);

const COLUMN_DEFAULTS = {
  item_created_at: false,
  item_modified_at: false,
  invoice_created_at: true,
  customer_type: true,
  id: false
}

const Reports = (props) => {

  const {
    farms,
    farmsLoading,
    strategicAccounts,
    strategicAccountsLoading
  } = props


  const { runProtected, state: userData } = useContext(LoginContext);

  const [columnVisibility, setColumnVisibility] = usePersistedState(
    'reportsCols',
    COLUMN_DEFAULTS
  )

  const calendarRef = useRef();


  function capitalizeFirstLetter(val) {
    return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
  }

  const [sortModel, setSortModel] = useState([{ field: 'invoice_created_at', sort: 'asc' }])

  const [startDate, setStartDate] = useState(moment().startOf('year').toDate());
  const [endDate, setEndDate] = useState(moment().toDate());
  const [selectedFarms, setSelectedFarms] = useState([]);
  const [reportPreset, setReportPreset] = useState('ytd');

  const {
    reportData,
    reportLoading,
    reportError,
    reportRefetch,
  } = useReports({ selectedFarms, startDate, endDate });


  const [rows, setRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [bulkEligibility, setBulkEligibility] = useState('');
  const [bulkIneligibleReason, setBulkIneligibleReason] = useState('');
  const [snackbar, setSnackbar] = useState(null);
  const handleCloseSnackbar = () => setSnackbar(null);

  useEffect(() => {
    if (!reportData) {
      setRows([]);
      return;
    }
    setRows(reportData);
  }, [reportData]);

  useEffect(() => {
    if(startDate || endDate){
      reportRefetch()
    }
 
  }, [startDate, endDate, selectedFarms]);

  const handleBulkEligibilityUpdate = async () => {
    if (!bulkEligibility || rowSelectionModel.length === 0) return;
    try {
      const response = await RebatesClient.bulkUpdateItemEligibility(
        rowSelectionModel,
        bulkEligibility,
        bulkEligibility === 'INELIGIBLE' ? bulkIneligibleReason : null
      );
      if (response.ok) {
        setRowSelectionModel([]);
        setBulkEligibility('');
        setBulkIneligibleReason('');
        reportRefetch();
        setSnackbar({ children: 'Bulk update complete', severity: 'success' });
      } else {
        setSnackbar({ children: 'Bulk update failed', severity: 'error' });
      }
    } catch (error) {
      console.error('Bulk update failed:', error);
      setSnackbar({ children: 'Bulk update failed', severity: 'error' });
    }
  };

  const columns = [

    { field: 'id', headerName: 'id', width: 90 },
    ...true ? [{
      field: 'strategic_acct',
      headerName: 'Strategic Account',
      width: 150,
      type: 'text',
      valueGetter: (_, row) => {
        return `${row?.parent_strategic_acct?.name}`
      },
    },
    {
      field: 'customer_type',
      headerName: 'Customer Type',
      width: 150,
      type: 'singleSelect',
      valueOptions: CUSTOMER_TYPE_FILTER_OPTIONS,
      valueGetter: (_, row) => row?.parent_strategic_acct?.customer_type || CUSTOMER_TYPE_NONE_FILTER_VALUE,
      valueFormatter: (value) =>
        !value || value === CUSTOMER_TYPE_NONE_FILTER_VALUE
          ? 'None'
          : CUSTOMER_TYPE_LABELS[value],
      renderCell: (params) => {
        const customerType =
          params?.row?.parent_strategic_acct?.customer_type ||
          CUSTOMER_TYPE_NONE_FILTER_VALUE;
        if (customerType === CUSTOMER_TYPE_NONE_FILTER_VALUE) return "None";

        const chipColors = CUSTOMER_TYPE_CHIP_COLORS[customerType];
        if (!chipColors) return CUSTOMER_TYPE_LABELS[customerType] || customerType;

        return (
          <Chip
            label={CUSTOMER_TYPE_LABELS[customerType] || customerType}
            size="small"
            sx={{
              backgroundColor: chipColors.bg,
              color: chipColors.color,
              fontWeight: 500,
            }}
          />
        );
      },
    }]

      : [],

      ... reportData?.some(d=> d.parent_sub_entity_acct1) ? [{
        field: 'sub_entity_acct1',
        headerName: 'Sub Account',
        width: 150,
        type: 'text',
        valueGetter: (_, row) => {
          return `${row?.parent_sub_entity_acct1?.name || ""}`
        },
      }]
  
        : [],
        ... reportData?.some(d=> d.parent_sub_sub_entity_acct2) ? [{
          field: 'sub_sub_entity_acct2',
          headerName: 'Sub Account',
          width: 150,
          type: 'text',
          valueGetter: (_, row) => {
            return `${row?.parent_sub_sub_entity_acct2?.name || ""}`
          },
        }]
    
          : [],
  
          
        
    {
      field: 'farm_name',
      headerName: 'Entity',
      width: 175,
      type: 'text',
    },

    { field: 'dealer', headerName: 'Dealer', width: 220, type: 'text' },

    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 140,
      type: 'text',
    },
    {
      field: 'product_category',
      headerName: 'Product Category',
      width: 160,
      type: 'singleSelect',
      valueOptions: [
        { value: 'Machine', label: 'Machine' },
        { value: 'Aftermarket', label: 'Aftermarket' },
        { value: 'Both', label: 'Both' },
        { value: '__PRODUCT_CATEGORY_BLANK__', label: 'Blank' },
      ],
      valueGetter: (_, row) => row?.product_category || '__PRODUCT_CATEGORY_BLANK__',
      valueFormatter: (value) => value === '__PRODUCT_CATEGORY_BLANK__' ? '' : value,
    },
    {
      field: 'part',
      headerName: 'Part Name',
      width: 180,
      type: 'text',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 180,
      type: 'text',
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 140,
      type: 'number',
    },
    {
      field: 'retail_price',
      headerName: 'Retail Price',
      width: 140,
      type: 'currency',
      valueGetter: (_, row) => {
        return row?.retail_price ? `${row?.retail_price}` : ''
      },
      valueFormatter: (value) => {
        return currencyFormatted(value)
      }
    },
    {
      headerName: 'Ext Price',
      width: 140,
      type: 'currency',
      valueGetter: (_, row) => {
        return `${row?.quantity * row?.retail_price}`
      },
      valueFormatter: (value) => {
        return currencyFormatted(value)
      }
    },

    {
      field: 'eligibility',
      headerName: 'Item Eligibility',
      width: 140,
      valueGetter: (_, row) => {
        //I valley has marked something as “Competitor” the Strategic Account and Farm will see it as “Ineligible”. They don’t want customers to know they are farming competitor data. 
        return (userData.user_role == "FARM" || userData.user_role == "STRATEGIC_ACCOUNT") && row.eligibility == "COMPETITOR" ? 'Ineligible' : capitalizeFirstLetter(row.eligibility)
      },

    },
    {
      field: 'ineligible_reason',
      headerName: 'Ineligible Reason',
      width: 180,
      type: 'text',
      valueGetter: (_, row) => {
        return row?.ineligible_reason || ''
      },
      valueFormatter: (value) => {
        const labels = {
          NON_VALLEY_DEALER: 'Not Purchased from Valley Dealer',
          NON_QUALIFIYING_PART: 'Non-Qualifying Part',
          COMPETITOR: 'Competitor',
        }

        return labels[value] || value || ''
      },
    },
    {
      field: 'invoice_status',
      headerName: 'Invoice Status',
      width: 140,
      type: 'text',
      valueGetter: (_, row) => {
        return row?.invoice_status == "REJECTED" ? "Not Approved" : row?.invoice_status == "NO_PAYMENT" ? "No Payment" : capitalizeFirstLetter(row?.invoice_status)
      },
    },
    { field: 'item_created_at', headerName: 'Item Created', width: 190, type: 'dateTime', valueGetter: (value) => value && new Date(value), },

    { field: 'item_modified_at', headerName: 'Item Modifed', width: 190, type: 'dateTime', valueGetter: (value) => value && new Date(value), },

    { field: 'invoice_created_at', headerName: 'Invoice Created', width: 190, type: 'dateTime', valueGetter: (value) => value && new Date(value), },

  ]

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ height: '35px' }}>
        {userData?.isValmontUser && rowSelectionModel.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {rowSelectionModel.length} item{rowSelectionModel.length > 1 ? 's' : ''} selected
            </span>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={bulkEligibility}
                onChange={(e) => {
                  setBulkEligibility(e.target.value);
                  if (e.target.value === 'ELIGIBLE') setBulkIneligibleReason('');
                }}
                displayEmpty
                sx={{ fontSize: 14, height: 32 }}
              >
                <MenuItem value="" disabled>Set Eligibility</MenuItem>
                <MenuItem value="ELIGIBLE">Eligible</MenuItem>
                <MenuItem value="INELIGIBLE">Ineligible</MenuItem>
              </Select>
            </FormControl>
            {bulkEligibility === 'INELIGIBLE' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={bulkIneligibleReason}
                  onChange={(e) => setBulkIneligibleReason(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: 14, height: 32 }}
                >
                  <MenuItem value="" disabled>Select Reason</MenuItem>
                  <MenuItem value="NON_VALLEY_DEALER">Not Purchased from Valley Dealer</MenuItem>
                  <MenuItem value="NON_QUALIFIYING_PART">Non-Qualifying Part</MenuItem>
                  <MenuItem value="COMPETITOR">Competitor</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={!bulkEligibility || (bulkEligibility === 'INELIGIBLE' && !bulkIneligibleReason)}
              onClick={handleBulkEligibilityUpdate}
              sx={{ textTransform: 'none', fontSize: 14, height: 32 }}
            >
              Apply
            </Button>
          </Box>
        )}

        <GridToolbarFilterButton />
        <Box sx={{ flexGrow: 1 }} />
        <GridToolbarColumnsButton
          slotProps={{
            button: { title: 'Manage Columns' },
          }}
        />
        <GridToolbarExport
          slotProps={{
            tooltip: { title: 'Export data' },
            button: { variant: 'outlined' },
          }}
        />

      </GridToolbarContainer>
    );
  }

  return (
    <>
      <Filters
       farms={farms}
       strategicAccounts={strategicAccounts}
       startDate={startDate}
       setStartDate={setStartDate}
       setEndDate={setEndDate}
       endDate={endDate}
       selectedFarms={selectedFarms}
       setSelectedFarms={setSelectedFarms}
       multiFarmSelect={true}
       preset={reportPreset}
       onPresetChange={setReportPreset}
       >
      </Filters>

      <div className={css.dataGrid}>
        <DataGrid
          classes={{ root: css.dataGrid }}
          slots={{ toolbar: CustomToolbar, columnsPanel: ColumnsPanel }}
          slotProps={{ columnsPanel: { defaults: COLUMN_DEFAULTS } }}
          localeText={{ toolbarColumns: "Manage Columns" }}
          loading={farmsLoading || strategicAccountsLoading || reportLoading}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          sortModel={sortModel}
          onColumnVisibilityModelChange={(newVisibility) => setColumnVisibility(newVisibility)}
          columnVisibilityModel={columnVisibility}
          rows={rows}
          columns={columns}
          checkboxSelection={!!userData?.isValmontUser}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
        />
      </div>

      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}

    </>

  )


}

export default Reports;
